import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Document, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import { Buffer } from 'buffer';

import { createElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import EnumSelect from 'components/field/EnumSelect';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { setPrintLocale } from 'pages/Settings/Print/PrintSettingsSlice';
import usePrintUtils from 'pages/Settings/Print/hooks/usePrintUtils';

import { PRINT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { languages } from 'utils/localization';

import { pdfScales } from './MainTemplate';
import printStyles from './defaultStyles';
import usePrintLocale from './usePrintLocale';

/**
 * @author @omerrkosar
 */

const DispatchingUnitTemplate = ({ data }) => {
	const {
		date,
		dispatchingUnitId,
		companyLogoURL = '',
		totalWeight,
		items = [],
		deliveryCompanyLogoURL = '',
		totalPackageCount
	} = data;

	const pageOrientation = 'portrait';
	const { printLocale } = usePrintLocale();

	const { t } = useTranslation('translation', {
		lng: printLocale
	});

	const pageSize = 'A4';
	const pageScale = pdfScales[pageSize];
	const styles = StyleSheet.create({
		section: {
			padding: 2,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center'
		},
		header: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			marginBottom: 20
		},
		headerItem: {
			display: 'inline-flex'
		},
		headerImage: {
			width: 50 * pageScale,
			height: 50 * pageScale
		},
		headerTitle: {
			fontSize: 25 * pageScale,
			textAlign: 'center'
		},
		unitInfoSection: {
			marginTop: 10,
			marginBottom: 20
		},
		unitInfo: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			marginBottom: 10
		},
		unitInfoItem: {
			display: 'inline-flex',
			flexDirection: document.body.dir === 'rtl' ? 'row-reverse' : 'row'
		},
		table: {
			display: 'table',
			width: 'auto'
		},
		tableRow: {
			margin: 'auto',
			flexDirection: document.body.dir === 'rtl' ? 'row-reverse' : 'row'
		},
		tableCol: {
			width: '25%',
			border: 'none',
			borderLeftWidth: 0,
			borderTopWidth: 0,
			textAlign: document.body.dir === 'rtl' ? 'right' : 'left'
		},
		tableHead: {
			borderBottomWidth: 1
		},
		tableHeader: {
			fontWeight: 700,
			marginBottom: 5
		},
		tableCell: {
			textAlign: document.body.dir === 'rtl' ? 'right' : 'left',
			marginTop: 5,
			fontSize: 10,
			fontWeight: 400,
			color: '#000'
		},
		page: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-start',
			paddingTop: 20,
			paddingHorizontal: 20,
			fontSize: 10,
			fontFamily: printStyles.text.fontFamily,
			color: '#000',
			fontWeight: 600
		},
		container: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-start',
			width: '95%',
			height: '100%'
		},
		footer: {
			marginTop: 'auto',
			marginBottom: 10
		},
		footerCredential: {
			display: 'flex',
			flexDirection: document.body.dir === 'rtl' ? 'row-reverse' : 'row',
			justifyContent: 'center',
			alignItems: 'center',
			textAlign: 'center',
			marginBottom: 10
		},
		logo: {
			width: 19,
			height: 19,
			textAlign: 'center'
		},
		pageNumber: {
			textAlign: 'right'
		}
	});

	return (
		<Document>
			<Page size={pageSize} orientation={pageOrientation} style={styles.page} wrap>
				<View style={[styles.container]}>
					<View>
						{/** header */}
						<View style={[styles.header]}>
							<View>
								{!!deliveryCompanyLogoURL && (
									<Image key="dcLogo" src={deliveryCompanyLogoURL} style={styles.headerImage} />
								)}
							</View>
							<View>
								<Text style={styles.headerTitle}>{t('dispatchingList').toLocaleUpperCase()}</Text>
							</View>
							<View>
								{!!companyLogoURL && (
									<Image key="dcLogo" src={companyLogoURL} style={styles.headerImage} />
								)}
							</View>
						</View>
						{/** header */}
						{/** unitInfo */}
						<View style={[styles.unitInfoSection]}>
							<View style={[styles.unitInfo]}>
								<View style={[styles.unitInfoItem]}>
									<Text>{t('dispatchingUnitNumber')}&nbsp;:&nbsp;</Text>
									<Text>{dispatchingUnitId ?? '-'}</Text>
								</View>
								<View style={[styles.unitInfoItem]}>
									<Text>{t('date')}&nbsp;:&nbsp;</Text>
									<Text>{date ?? '-'}</Text>
								</View>
							</View>
							<View style={[styles.unitInfo]}>
								<View style={[styles.unitInfoItem]}>
									<Text>{t('totalPackageCount')}&nbsp;:&nbsp;</Text>
									<Text>{totalPackageCount ?? '-'}</Text>
								</View>
								<View style={[styles.unitInfoItem]}>
									<Text>{t('totalWeight')}&nbsp;:&nbsp;</Text>
									<Text>{totalWeight ?? '-'}</Text>
								</View>
							</View>
						</View>
						{/** unitInfo */}
						{/* Table */}
						<View style={styles.table}>
							<View style={[styles.tableRow, styles.tableHead]}>
								<View style={[styles.tableCol, { width: '30%' }]}>
									<Text style={[styles.tableCell, styles.tableHeader]}>{t('orderID')}</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={[styles.tableCell, styles.tableHeader]}>{t('shipmentNumber')}</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={[styles.tableCell, styles.tableHeader]}>{t('packageCount')}</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={[styles.tableCell, styles.tableHeader]}>{t('weightKG')}</Text>
								</View>
							</View>
							{items?.map((item, index) => (
								<View style={styles.tableRow} key={index}>
									<View style={[styles.tableCol, { width: '30%' }]}>
										<Text style={[styles.tableCell]}>{item?.orderID ?? '-'}</Text>
									</View>
									<View style={styles.tableCol}>
										<Text style={[styles.tableCell]}>{item?.shipmentNumber ?? '-'}</Text>
									</View>
									<View style={styles.tableCol}>
										<Text style={[styles.tableCell]}>{item?.packageCount ?? '-'}</Text>
									</View>
									<View style={styles.tableCol}>
										<Text style={[styles.tableCell]}>{item?.weight ?? '-'}</Text>
									</View>
								</View>
							))}
						</View>
						{/* Table */}
					</View>
					<View style={[styles.footer]} fixed>
						<View style={[styles.footerCredential]}>
							<Text> {t('poweredBy')} </Text>
							<Image
								src="https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png"
								style={styles.logo}
							/>
						</View>
						<View style={[styles.pageNumber]}>
							<Text
								style={styles.pageNumber}
								render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
								fixed
							/>
						</View>
					</View>
				</View>
			</Page>
		</Document>
	);
};

const DispatchingUnit = NiceModal.create(({ id, defaultLang }) => {
	const [printSettings, setPrintSettings] = useState({});
	const [documentData, setDocumentData] = useState(null);
	const axios = useAxiosPrivate();
	const modal = useModal();
	const { handlePDFDownload, isLoading, pdfFile, mergePDF } = usePrintUtils();
	const { getLocaleDate } = useLocale();
	const { showError } = useAlert();

	const langs = useMemo(() => languages.map((language) => language.code), []);
	const dispatch = useDispatch();
	const { printLocale, fetchLanguage } = usePrintLocale();
	const { mobile = false } = useSelector((state) => state?.layout);

	const errors = useMemo(() => ['error.PM001'], []);

	const fetchDispatchUnitList = async (id) =>
		await axios
			.post(PRINT.GET_DISPATCHINGING_LIST, {
				dispatchingUnitId: Number(id)
			})
			.then((response) => {
				const { data } = response;
				setDocumentData({
					...data,
					date: data.date ? getLocaleDate(data.date) : '-'
				});
			})
			.catch((error) => {
				if (window.ReactNativeWebView) {
					if (error?.response?.status === 401) {
						window.ReactNativeWebView.postMessage('tokenExpired');
					} else {
						window.ReactNativeWebView.postMessage('errorMsg.printDispatchingList');
					}
				}
				AppLogger.error(error);
				if (errors.includes(error?.response?.data?.code)) {
					showError({
						message: error?.response?.data?.code
					});
					modal.remove();

					return;
				} else {
					showError({
						message: 'errorMsg.printDispatchingList'
					});
				}
				modal.remove();

				return null;
			});

	const createPDF = async (dispatchList) => {
		if (!dispatchList) return;

		await mergePDF(async (merger) => {
			const pdfBlob = await pdf(
				createElement(DispatchingUnitTemplate, {
					data: dispatchList
				})
			).toBlob();
			await merger.add(pdfBlob);

			if (window.ReactNativeWebView) {
				const buffer = await merger.saveAsBuffer();
				const u8 = new Uint8Array(buffer);
				const b64 = Buffer.from(u8).toString('base64');
				window.ReactNativeWebView.postMessage(b64);
			} else {
				const mergedPdf = await merger.saveAsBlob();

				return mergedPdf;
			}
		});
	};

	useEffect(() => {
		fetchDispatchUnitList(id);
	}, [id]);

	useEffect(() => {
		if (defaultLang) {
			dispatch(setPrintLocale(defaultLang));
		}
	}, [defaultLang]);

	useEffect(() => {
		setPrintSettings((prevState) => ({
			...prevState,
			language: printLocale
		}));
	}, [printLocale]);

	useEffect(() => {
		if (!documentData) return;
		fetchLanguage(printLocale).then(() => createPDF(documentData));
	}, [printSettings, documentData]);

	useEffect(() => {
		if (pdfFile && mobile) {
			handlePDFDownload(pdfFile, `dispatching-list-${id}`);
			modal.remove();
		}
	}, [pdfFile, mobile]);

	return !mobile ? (
		<Modal closeButton maxWidth="x-large" fullwidth className="modal-internationalProforma">
			<ModalTitle>
				<Typography className="me-3" variant="p1">
					dispatchingList
				</Typography>
				<FormItem colSpan={{ md: 2 }}>
					<EnumSelect
						valueRemovable={false}
						data={langs}
						value={printLocale}
						setValue={(value) => {
							dispatch(setPrintLocale(value));
						}}
						searchable={false}
					/>
				</FormItem>
			</ModalTitle>
			<ModalContent
				style={{
					height: '80dvh'
				}}>
				{pdfFile && !mobile ? (
					<div
						style={{
							height: '100%'
						}}
						dangerouslySetInnerHTML={{
							__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
						}}
					/>
				) : (
					<Loading />
				)}
			</ModalContent>
		</Modal>
	) : (
		isLoading && <Loading />
	);
});

export default DispatchingUnit;
